import React, { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import { money, today } from '../lib/format';
import type { Property, Task } from '../lib/types';
import { Stat, type Ctx } from '../main';
import { DEFAULT_CENTRE, findCity, useLeafletMap } from '../components/Places';
import { PropertyDrawer, StatusTag, typeLine } from './Properties';

const PIN: Record<Property['status'], string> = {
  Operating: '#0F6B5C', UnderTender: '#B57C1E', Maintenance: '#AC4034', Released: '#728A92',
};

const esc = (s: string): string =>
  s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));

/**
 * Where everything is, and what is wrong there — on the real streets.
 *
 * Pins come from each property's own coordinates, which for a building or a
 * floor are its building's. A property without them is listed beside the map
 * rather than guessed at: a pin in the wrong district is worse than no pin,
 * because somebody drives to it. Floors of one tower share a pin position, so
 * they are drawn as one pin with the tower's name and everything in it summed.
 *
 * Each pin's ring is the share of rooms let, its colour the property's state,
 * a red number the overdue work there, and a pulse a critical emergency.
 */
export function PortfolioMap({ ctx, tasks }: { ctx: Ctx; tasks: Task[] }): React.JSX.Element {
  const cities = useMemo(() => {
    const counts = new Map<string, number>();
    for (const p of ctx.properties) counts.set(p.city || 'Unknown', (counts.get(p.city || 'Unknown') ?? 0) + 1);
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([c]) => c);
  }, [ctx.properties]);
  const [city, setCity] = useState<string>(cities[0] ?? 'all');
  const [selected, setSelected] = useState<string | null>(null);
  const [opened, setOpened] = useState<Property | null>(null);

  const inCity = ctx.properties.filter((p) => city === 'all' || (p.city || 'Unknown') === city);
  const placed = inCity.filter((p) => p.latitude !== null && p.longitude !== null);
  const unplaced = inCity.filter((p) => p.latitude === null || p.longitude === null);

  const now = today();
  const openAt = (ids: string[]): Task[] => tasks.filter((t) => t.propertyId !== null && ids.includes(t.propertyId));
  const lateAt = (ids: string[]): number => openAt(ids).filter((t) => t.dueDate < now).length;
  const sosAt = (ids: string[]): number => openAt(ids).filter((t) => t.severity === 'Critical').length;

  // one pin per spot: the floors of a tower stand at the same coordinates
  const spots = useMemo(() => {
    const by = new Map<string, Property[]>();
    for (const p of placed) {
      const key = p.buildingId ?? `${p.latitude},${p.longitude}`;
      by.set(key, [...(by.get(key) ?? []), p]);
    }
    return [...by.entries()].map(([key, ps]) => {
      const building = ctx.buildings.find((b) => b.id === ps[0]!.buildingId);
      const worst = (['Maintenance', 'UnderTender', 'Operating', 'Released'] as const).find((s) => ps.some((p) => p.status === s))!;
      return {
        key, properties: ps, name: ps.length > 1 && building ? building.name : ps[0]!.name,
        lat: ps[0]!.latitude!, lng: ps[0]!.longitude!, status: worst,
        rooms: ps.reduce((a, p) => a + p.roomCount, 0), let_: ps.reduce((a, p) => a + p.occupiedCount, 0),
      };
    });
  }, [placed, ctx.buildings]);
  const spot = spots.find((s) => s.key === selected) ?? null;

  const el = useRef<HTMLDivElement>(null);
  const map = useLeafletMap(el, DEFAULT_CENTRE, 11);
  const layer = useRef<L.LayerGroup | null>(null);

  // pins: redrawn whenever what they show changes
  useEffect(() => {
    if (!map) return;
    layer.current?.remove();
    const group = L.layerGroup().addTo(map);
    for (const s of spots) {
      const ids = s.properties.map((p) => p.id);
      const late = lateAt(ids), sos = sosAt(ids);
      const frac = s.rooms ? s.let_ / s.rooms : 0;
      const label = s.name.length > 24 ? `${s.name.slice(0, 23)}…` : s.name;
      const icon = L.divIcon({
        className: '', iconSize: [34, 34], iconAnchor: [17, 17],
        html: `<div class="lpin${selected === s.key ? ' on' : ''}${sos ? ' sos' : ''}" role="button" aria-label="${esc(s.name)}">`
          + `<span class="ring" style="--c:${PIN[s.status]};--f:${Math.max(frac, 0.02)}"></span>`
          + `${late ? `<b>${late}</b>` : ''}<em>${esc(label)}${s.properties.length > 1 ? ` · ${s.properties.length}` : ''}</em></div>`,
      });
      L.marker([s.lat, s.lng], { icon, keyboard: true, title: s.name })
        .on('click', () => setSelected(s.key))
        .addTo(group);
    }
    layer.current = group;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, spots, selected, tasks]);

  // the view: the chosen city's pins, else the city itself from the places list
  useEffect(() => {
    if (!map) return;
    if (spots.length) {
      map.fitBounds(L.latLngBounds(spots.map((s) => [s.lat, s.lng] as [number, number])),
        { padding: [50, 50], maxZoom: 16 });
      return;
    }
    const c = findCity(ctx.locations, null, city === 'all' ? undefined : city);
    if (c?.bbox) map.fitBounds([[c.bbox[0], c.bbox[1]], [c.bbox[2], c.bbox[3]]]);
    else if (c?.latitude != null && c.longitude != null) map.setView([c.latitude, c.longitude], 11);
    // when the city changes, or the pins first arrive — not on every refresh
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, city, spots.length]);

  const chooseCity = (c: string): void => { setCity(c); setSelected(null); };
  const rooms = inCity.reduce((a, p) => a + p.roomCount, 0);
  const let_ = inCity.reduce((a, p) => a + p.occupiedCount, 0);
  const ids = spot?.properties.map((p) => p.id) ?? [];

  return (
    <>
      <div className="bar">
        <button className={`btn sm ${city === 'all' ? 'pri' : ''}`} onClick={() => chooseCity('all')}>All cities</button>
        {cities.map((c) => (
          <button key={c} className={`btn sm ${city === c ? 'pri' : ''}`} onClick={() => chooseCity(c)}>{c}</button>
        ))}
      </div>

      <div className="grid g3 mapstats" style={{ marginBottom: 14 }}>
        <Stat label="Sites" value={inCity.length} foot={unplaced.length ? `${unplaced.length} not yet on the map` : 'All on the map'} />
        <Stat label="Occupancy" value={rooms ? `${Math.round((let_ / rooms) * 100)}%` : '—'} tone="good" foot={`${let_} of ${rooms} rooms`} />
        <Stat label="Overdue here" value={lateAt(inCity.map((p) => p.id))}
          tone={lateAt(inCity.map((p) => p.id)) ? 'hot' : undefined} />
      </div>

      <div className="mapwrap">
        <div className="card mapcard">
          <div ref={el} className="leafmap" />
          <div className="maplegend sub">
            {(Object.keys(PIN) as Property['status'][]).map((s) => (
              <span key={s}><i style={{ background: PIN[s] }} />{s === 'UnderTender' ? 'Under tender' : s}</span>
            ))}
            <span style={{ marginLeft: 'auto' }}>Ring: rooms let · red number: overdue work</span>
          </div>
          {placed.length === 0 && (
            <div className="mapempty">
              <b>Nothing to place yet</b>
              <span>Give a property its location — Edit property, or Edit the building — and it appears here.</span>
            </div>
          )}
        </div>

        <div className="mapside">
          {spot ? (
            <div className="card"><div className="bd">
              <div className="between"><h3 style={{ fontSize: 15 }}>{spot.name}</h3><StatusTag status={spot.status} /></div>
              <div className="sub" style={{ marginTop: 3 }}>
                {[spot.properties[0]!.address, spot.properties[0]!.district, spot.properties[0]!.city].filter(Boolean).join(', ')}
              </div>
              <dl className="kv" style={{ marginTop: 12 }}>
                <dt>Rooms let</dt><dd className="mono">{spot.let_}/{spot.rooms}</dd>
                {ctx.canManage && <><dt>Rent roll</dt><dd className="mono">{money(spot.properties.reduce((a, p) => a + Number(p.rentRoll), 0))}</dd></>}
                <dt>Open work</dt><dd className="mono">{openAt(ids).length}</dd>
                <dt>Overdue</dt><dd className={`mono ${lateAt(ids) ? 'late' : ''}`}>{lateAt(ids)}</dd>
              </dl>
              {sosAt(ids) > 0 && (
                <div className="note bad" style={{ marginTop: 10 }}>
                  {sosAt(ids) === 1 ? 'A critical emergency is open here.' : `${sosAt(ids)} critical emergencies are open here.`}
                </div>
              )}
              {spot.properties.map((p) => (
                <button key={p.id} className="rowcard" style={{ marginTop: 10 }} onClick={() => setOpened(p)}>
                  <span className="body"><span className="tt" style={{ fontSize: 13, display: 'block' }}>{p.name}</span>
                    <span className="sub">{typeLine(p, ctx.buildings)} · {p.occupiedCount}/{p.roomCount} let</span></span>
                  <StatusTag status={p.status} />
                </button>
              ))}
              <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
                <button className="btn sm danger w" onClick={() => ctx.newTask({ emergency: true, propertyId: spot.properties[0]!.id })}>
                  Report an emergency
                </button>
                <a className="btn sm" target="_blank" rel="noreferrer"
                  href={`https://www.google.com/maps/dir/?api=1&destination=${spot.lat},${spot.lng}`}>Directions</a>
              </div>
              {openAt(ids).slice(0, 5).map((t) => (
                <button key={t.id} className="rowcard" style={{ marginTop: 10 }} onClick={() => ctx.open(t)}>
                  <span className="body"><span className="tt" style={{ fontSize: 13, display: 'block' }}>{t.name}</span>
                    <span className={`sub ${t.dueDate < now ? 'late' : ''}`}>{t.dueDate < now ? `overdue since ${t.dueDate}` : `due ${t.dueDate}`}</span></span>
                </button>
              ))}
            </div></div>
          ) : (
            <div className="note">Pick a pin for its rooms, money and open work.</div>
          )}

          {unplaced.length > 0 && (
            <div className="card" style={{ marginTop: 14 }}>
              <div className="hd"><h3>Not on the map yet</h3></div>
              <div className="bd">
                {unplaced.map((p) => (
                  <button key={p.id} className="rowcard" onClick={() => setOpened(p)}>
                    <span className="body"><span className="tt" style={{ fontSize: 13, display: 'block' }}>{p.name}</span>
                      <span className="sub">{p.district || p.city} · set its position</span></span>
                    {lateAt([p.id]) > 0 && <span className="tag t-bad">{lateAt([p.id])} late</span>}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {opened && (
        <PropertyDrawer ctx={ctx} property={opened} onClose={() => setOpened(null)}
          onChanged={async () => { setOpened(null); await ctx.refresh(); }} />
      )}
    </>
  );
}
